import { memo, useEffect, useState } from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import Head from 'next/head'
import Router from 'next/router'
import nProgress from 'nprogress'
import { Toaster } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { CacheProvider } from '@emotion/react'

//
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

//
import { AuthConsumer, AuthProvider } from '../contexts/auth-context'
import { NotificationProvider } from '../contexts/notification-context'
import {
    SettingsConsumer,
    SettingsProvider
} from '../contexts/settings-context'
import { TermsOfServiceModal } from '../components/dashboard/terms-of-service-modal'

//
import api from '../api'
import { store } from '../store'
import { gtmConfig, siteConfig } from '../config'

//
import InnerApp from './_inner_app'

//
import Notification from '../components/notification'
import { SplashScreen } from '../components/splash-screen'
//
import { setLanguage } from '../i18n'
import { gtm } from '../lib/gtm'
import { createTheme } from '../theme'
import { createEmotionCache } from '../utils/create-emotion-cache'

//
import './main.css'
//
Router.events.on('routeChangeStart', nProgress.start)
Router.events.on('routeChangeError', nProgress.done)
Router.events.on('routeChangeComplete', nProgress.done)

//
const clientSideEmotionCache = createEmotionCache()

const App = memo((props) => {
    const getGoogleMaps = () => {
        if ((window.google || {}).maps) {
            console.log('Google Maps In place')
            return
        }
        console.log('Google Maps Loading')
        // Load the Google Maps API
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_MAP_KEY}&libraries=places,visualization&v=3.64`
        script.async = true
        document.body.appendChild(script)
    }

    const { t } = useTranslation()
    const {
        Component,
        emotionCache = clientSideEmotionCache,
        pageProps
    } = props

    const getLayout = Component.getLayout ?? ((page) => page)

    useEffect(() => {
        gtm.initialize(gtmConfig)

        setLanguage(siteConfig.lang)
        getGoogleMaps()
    }, [])

    useEffect(() => {
        api.setIsCurrentPagePublic(Component.isPublicPage === true)
    }, [Component])

    return (
        <CacheProvider value={emotionCache}>
            <Head>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <title>{t('meta_title_suffix')}</title>
                <link
                    rel="stylesheet"
                    href="https://fonts.googleapis.com/icon?family=Material+Icons"
                />

                <meta name="robots" content="noindex, nofollow, noarchive" />

                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800&amp;display=swap"
                    rel="stylesheet"
                />
                <script
                    src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"
                    async
                />
                {['development', 'staging', 'dev', 'stg'].includes(
                    process.env.NEXT_PUBLIC_ENV
                ) && (
                    <script
                        async
                        src={`${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT}/__devsquad-sidecar/assets/js`}
                    />
                )}
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href="/apple-touch-icon.png"
                />
                <link rel="icon" href="/favicon.ico" />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href="/favicon-32x32.png"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="16x16"
                    href="/favicon-16x16.png"
                />
                <meta name="theme-color" content="#111827" />
            </Head>
            <ReduxProvider store={store}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <NotificationProvider>
                        <AuthProvider>
                            <SettingsProvider>
                                <SettingsConsumer>
                                    {({ settings }) => (
                                        <ThemeProvider
                                            theme={createTheme({
                                                direction: settings.direction,
                                                responsiveFontSizes:
                                                    settings.responsiveFontSizes,
                                                mode: siteConfig.theme
                                            })}
                                        >
                                            <CssBaseline />
                                            <Notification />
                                            <Toaster position="top-center" />
                                            {/* <SettingsButton /> */}
                                            <TermsOfServiceModal />
                                            <InnerApp>
                                                <AuthConsumer>
                                                    {(auth) =>
                                                        !auth.isInitialized ? (
                                                            <SplashScreen />
                                                        ) : (
                                                            getLayout(
                                                                <Component
                                                                    {...pageProps}
                                                                />
                                                            )
                                                        )
                                                    }
                                                </AuthConsumer>
                                            </InnerApp>
                                        </ThemeProvider>
                                    )}
                                </SettingsConsumer>
                            </SettingsProvider>
                        </AuthProvider>
                    </NotificationProvider>
                </LocalizationProvider>
            </ReduxProvider>
        </CacheProvider>
    )
})

export default App
